// ── UPLOAD RECOVERY ──
// Drives a queued upload (from utils/uploadQueue.js) through four steps:
//   1. /sessions/start            (if no sessionId yet)
//   2. /audio                     (PUT bytes into R2)
//   3. /complete                  (mark session ready, kick transcription)
//   4. /verify-audio              (HEAD check R2 — confirms bytes landed)
//
// Step 4 is the critical safeguard the user asked for: the local
// IndexedDB copy of the recording is the storyteller's irreplaceable
// fallback. We only delete it once R2 confirms it has the same number
// of bytes. If verification fails, audioUploaded is reset so the next
// retry re-uploads.
//
// Each step's success is persisted on the queue item so a retry only
// runs the steps that haven't already succeeded. Idempotent.
//
// Used by:
//   - StorytellerPage at end of recording (one immediate try)
//   - useUploadRetry hook in the background (periodic + on app mount)

import { sessions } from './api.js';
import { updatePending, removePending } from '../utils/uploadQueue.js';

const UUID_LIKE = /^[0-9a-f-]{32,}$/i;

// Process one queue item, advancing it through whichever steps remain.
// Throws on the first failure; the caller is responsible for incrementing
// `attempts` and `lastTried` and leaving the item in the queue for retry.
//
// Three flow variants depending on `item.kind`:
//   - 'segment'    — only uploads the per-segment audio (no /complete).
//   - 'checkpoint' — orphan-recovery path: uploads as a segment + calls
//                    /complete to finalize a session that never had its
//                    proper end-of-recording flow run.
//   - 'session' (default) — the original full-session flow.
export async function processQueueItem(item) {
  if (!item) throw new Error('processQueueItem: missing item');
  const kind = item.kind || 'session';
  if (kind === 'segment')    return processSegmentItem(item);
  if (kind === 'checkpoint') return processCheckpointItem(item);
  return processSessionItem(item);
}

// Per-segment upload (interrupted segment that didn't make it through during
// the live session). Just push the bytes to /audio with the right
// segmentIndex and verify. /complete is NOT called here — the session's own
// 'session' queue item (or a future checkpoint recovery) is responsible
// for finalization.
async function processSegmentItem(item) {
  if (!item.sessionId || !UUID_LIKE.test(item.sessionId)) {
    // A segment without a session is orphaned and unrecoverable — drop it
    // rather than retry forever.
    console.warn('[Recovery] segment item without sessionId, dropping:', item.id);
    await removePending(item.id);
    return { ok: false, dropped: true };
  }
  if (!item.audioUploaded && item.audioBlob) {
    const segIdx = (typeof item.segmentIndex === 'number') ? item.segmentIndex : undefined;
    const uploadRes = await sessions.uploadAudio(item.sessionId, item.audioBlob, segIdx);
    item.audioUploaded = true;
    if (uploadRes && uploadRes.audioKey) item.audioKey = uploadRes.audioKey;
    await updatePending(item.id, { audioUploaded: true, audioKey: item.audioKey || null });
  }
  if (!item.audioVerified) {
    const verify = await sessions.verifyAudio(item.sessionId);
    const localSize = item.audioBlob && item.audioBlob.size ? item.audioBlob.size : 0;
    const serverSize = verify && verify.sizeBytes ? verify.sizeBytes : 0;
    // For segments verifyAudio only HEADs the LATEST segment, which is what
    // we just uploaded — so the size check is meaningful here too.
    if (!verify || !verify.exists) {
      await updatePending(item.id, { audioUploaded: false });
      throw new Error('Segment audio not in R2 yet; will retry upload.');
    }
    if (localSize > 0 && serverSize < localSize * 0.9) {
      await updatePending(item.id, { audioUploaded: false });
      throw new Error('Segment size mismatch (local ' + localSize + ' vs server ' + serverSize + '); will retry.');
    }
    await updatePending(item.id, { audioVerified: true, verifiedSizeBytes: serverSize });
  }
  await removePending(item.id);
  return { ok: true, sessionId: item.sessionId, segment: true };
}

// Orphan-checkpoint recovery: the storyteller's app died mid-recording
// (browser crash, OS kill, etc.) and the 30s rolling checkpoint is the
// only record of the audio captured AFTER the last interruption / segment.
// We upload it as a segment AND call /complete so the session is finalized
// rather than stuck in 'recording' state forever. Conversation history is
// empty because /exchange already wrote per-turn rows on the server side —
// those are still there.
async function processCheckpointItem(item) {
  if (!item.sessionId || !UUID_LIKE.test(item.sessionId)) {
    console.warn('[Recovery] checkpoint without sessionId, dropping:', item.id);
    await removePending(item.id);
    return { ok: false, dropped: true };
  }
  // Step A: upload as a segment.
  if (!item.audioUploaded && item.audioBlob) {
    const segIdx = (typeof item.segmentIndex === 'number') ? item.segmentIndex : 0;
    const uploadRes = await sessions.uploadAudio(item.sessionId, item.audioBlob, segIdx);
    item.audioUploaded = true;
    if (uploadRes && uploadRes.audioKey) item.audioKey = uploadRes.audioKey;
    await updatePending(item.id, { audioUploaded: true, audioKey: item.audioKey || null });
  }
  // Step B: finalize the session. The backend pulls audio_segments off the
  // row, so we don't need to pass audioKeys — but we DO need to call
  // /complete, otherwise the session stays in 'processing' / 'recording'.
  if (!item.completed) {
    await sessions.complete(item.sessionId, {
      audioKey: item.audioKey || null,
      durationSecs: item.durationSecs || 0,
      conversationHistory: [], // /exchange already saved any turns the storyteller spoke
    });
    item.completed = true;
    await updatePending(item.id, { completed: true });
  }
  // Step C: verify the audio.
  if (!item.audioVerified) {
    const verify = await sessions.verifyAudio(item.sessionId);
    const localSize = item.audioBlob && item.audioBlob.size ? item.audioBlob.size : 0;
    const serverSize = verify && verify.sizeBytes ? verify.sizeBytes : 0;
    if (!verify || !verify.exists) {
      await updatePending(item.id, { audioUploaded: false });
      throw new Error('Checkpoint audio not in R2; will retry upload.');
    }
    if (localSize > 0 && serverSize < localSize * 0.9) {
      await updatePending(item.id, { audioUploaded: false });
      throw new Error('Checkpoint size mismatch (' + localSize + ' vs ' + serverSize + ')');
    }
    await updatePending(item.id, { audioVerified: true, verifiedSizeBytes: serverSize });
  }
  await removePending(item.id);
  return { ok: true, sessionId: item.sessionId, checkpoint: true };
}

async function processSessionItem(item) {
  // The original full-session flow lives below — unchanged.

  // Step 1: ensure we have a real session id.
  if (!item.sessionId || !UUID_LIKE.test(item.sessionId)) {
    const startBody = { mode: item.mode || 'interview' };
    if (item.storytellerAuthUserId) startBody.storytellerAuthUserId = item.storytellerAuthUserId;
    const res = await sessions.start(startBody);
    const newId = res?.session?.id || res?.id;
    if (!newId || !UUID_LIKE.test(newId)) {
      throw new Error('Session start returned no valid id');
    }
    item.sessionId = newId;
    await updatePending(item.id, { sessionId: newId });
  }

  // Step 2: upload the audio blob (if not already uploaded). The /audio
  // route returns the R2 key it stored under; we persist it on the queue
  // item so /complete can be told where the audio actually lives. Without
  // this, /complete's `audio_url: audioKey ? ... : null` would null out
  // the row's audio_url, AND processSessionAsync would skip transcribeAudio
  // (no Whisper run, no usage row).
  //
  // Multi-segment: if priorSegmentKeys is set on the item (recorded during
  // a paused-and-resumed interview where earlier segments already uploaded
  // synchronously), this queued blob is the FINAL segment. We pass its
  // segmentIndex so the backend appends instead of overwriting.
  if (!item.audioUploaded && item.audioBlob) {
    const segIdx = (typeof item.finalSegmentIndex === 'number') ? item.finalSegmentIndex : undefined;
    const uploadRes = await sessions.uploadAudio(item.sessionId, item.audioBlob, segIdx);
    item.audioUploaded = true;
    if (uploadRes && uploadRes.audioKey) {
      item.audioKey = uploadRes.audioKey;
    }
    await updatePending(item.id, { audioUploaded: true, audioKey: item.audioKey || null });
  }

  // Step 3: finalize via /complete. The conversation history populates
  // per-turn transcript rows; the audioKey(s) tell the backend to run
  // Whisper transcription on the uploaded recording.
  if (!item.completed) {
    // Multi-segment: combine prior keys + final upload into the audioKeys
    // array. The backend's /complete also pulls audio_segments off the row
    // as a fallback, so this is belt-and-suspenders.
    let audioKeys = null;
    if (Array.isArray(item.priorSegmentKeys) && item.priorSegmentKeys.length > 0) {
      audioKeys = [...item.priorSegmentKeys];
      if (item.audioKey) audioKeys.push(item.audioKey);
    }
    await sessions.complete(item.sessionId, {
      audioKey: item.audioKey || null,
      audioKeys: audioKeys,
      durationSecs: item.durationSecs || 0,
      conversationHistory: item.conversationHistory || [],
    });
    item.completed = true;
    await updatePending(item.id, { completed: true });
  }

  // Step 4: verify R2 actually has the audio before discarding the local
  // copy. This is the critical safeguard — the IndexedDB blob is the only
  // remaining copy of the storyteller's recording. We don't delete it
  // until the server confirms R2 has the same bytes.
  if (!item.audioVerified) {
    let verify;
    try {
      verify = await sessions.verifyAudio(item.sessionId);
    } catch (err) {
      // Couldn't reach the verification endpoint. Leave the blob in place
      // and bubble up — the retry hook will try again later.
      throw new Error('verify-audio call failed: ' + (err.message || err));
    }

    const localSize = item.audioBlob && item.audioBlob.size ? item.audioBlob.size : 0;
    const serverSize = verify && verify.sizeBytes ? verify.sizeBytes : 0;

    if (!verify || !verify.exists) {
      // R2 doesn't have it. Reset audioUploaded so the next retry pushes
      // the blob again. Leave the queue item intact — the retry hook will
      // pick it back up on the next tick.
      await updatePending(item.id, { audioUploaded: false });
      throw new Error('Audio not in R2 yet (HEAD said missing); will retry upload.');
    }

    // Size check: require server bytes ≥ 90% of local bytes. (Container
    // metadata can shave a tiny amount during R2 ingestion in some
    // pathological cases — accept a small floor; reject anything that
    // looks like a half-uploaded blob.)
    if (localSize > 0 && serverSize < localSize * 0.9) {
      console.warn('[Recovery] Size mismatch on verify — local=' + localSize + ' server=' + serverSize);
      await updatePending(item.id, { audioUploaded: false });
      throw new Error('Audio size mismatch (local ' + localSize + ' vs server ' + serverSize + '); will retry upload.');
    }

    item.audioVerified = true;
    await updatePending(item.id, {
      audioVerified: true,
      verifiedSizeBytes: serverSize,
    });
  }

  // All four steps done AND verified. Now safe to drop from the queue —
  // R2 has confirmed bytes, so the local blob is no longer the only copy.
  await removePending(item.id);
  return { ok: true, sessionId: item.sessionId, verifiedSizeBytes: item.verifiedSizeBytes };
}
